/**
 * Event consultation tracking utilities for Events page
 * Tracks event consultation intents, Calendly interactions, and event contact modals
 */

/**
 * Get current language from localStorage or path
 * @returns The current language code (en, de, fr, etc.)
 */
const getCurrentLanguage = (): string => {
  if (typeof window === 'undefined') return 'en';
  
  // Try to get from localStorage first
  const savedLang = localStorage.getItem('preferred-language');
  if (savedLang) return savedLang;
  
  // Fallback to path detection
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const langCode = pathParts[0];
  if (['de', 'fr', 'ja', 'zh', 'ko'].includes(langCode)) return langCode;
  
  return 'en';
};

/**
 * Track event consultation intent (when user clicks to inquire about events)
 * Follows proper variable clearing pattern to prevent data pollution
 * 
 * @param eventType - The type of event ('wedding', 'corporate', 'private_party', 'general')
 * @param buttonText - The text on the button clicked
 * @param buttonLocation - Where the button is located on the page
 * @param consultationMethod - How the user is reaching out ('calendly', 'contact_modal')
 * @param serviceCategory - Optional: The specific service category (for tabbed section)
 */
export const trackEventConsultation = (
  eventType: 'wedding' | 'corporate' | 'private_party' | 'general',
  buttonText: string,
  buttonLocation: string,
  consultationMethod: 'calendly' | 'contact_modal',
  serviceCategory?: string
): void => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    
    // ============================================
    // STEP 1: CLEAR ALL VARIABLES (prevents data pollution)
    // ============================================
    window.dataLayer.push({
      // Clear button tracking variables
      button_text: undefined,
      button_location: undefined,
      
      // Clear modal tracking variables
      modal_type: undefined,
      modal_name: undefined,
      parent_modal: undefined,
      page_location: undefined,
      
      // Clear booking tracking variables
      room_type: undefined,
      
      // Clear contact action variables
      contact_type: undefined,
      contact_source: undefined,
      event_category: undefined,
      event_label: undefined,
      phone_number: undefined,
      email_address: undefined,
      whatsapp_number: undefined,
      region: undefined,
      destination: undefined,
      booking_method: undefined,
      
      // Clear event consultation variables (will be reset below)
      event_type: undefined,
      consultation_method: undefined,
      service_category: undefined,
      
      // Clear form variables
      form_name: undefined,
      form_location: undefined,
      
      // Clear language variables
      from_language: undefined,
      to_language: undefined,
    });
    
    // ============================================
    // STEP 2: PUSH THE ACTUAL EVENT
    // ============================================
    window.dataLayer.push({
      event: 'event_consultation_intent',
      event_type: eventType,
      button_text: buttonText,
      button_location: buttonLocation,
      consultation_method: consultationMethod,
      ...(serviceCategory && { service_category: serviceCategory }),
      page_path: window.location.pathname,
      user_language: getCurrentLanguage(),
    });
  }
};

/**
 * Track Calendly embed interactions
 * Tracks when users interact with the embedded Calendly widget
 * 
 * @param action - The action type ('view', 'interaction', 'scheduled')
 * @param eventType - The type of event being scheduled
 * @param location - Where the Calendly embed is located
 */
export const trackCalendlyEmbed = (
  action: 'view' | 'interaction' | 'scheduled',
  eventType: 'general' | 'wedding' | 'corporate' | 'private_party',
  location: string
): void => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    
    // Clear all variables first
    window.dataLayer.push({
      // Clear button tracking variables
      button_text: undefined,
      button_location: undefined,
      
      // Clear modal tracking variables
      modal_type: undefined,
      modal_name: undefined,
      parent_modal: undefined,
      page_location: undefined,
      
      // Clear booking tracking variables
      room_type: undefined,
      
      // Clear contact action variables
      contact_type: undefined,
      contact_source: undefined,
      event_category: undefined,
      event_label: undefined,
      phone_number: undefined,
      email_address: undefined,
      whatsapp_number: undefined,
      region: undefined,
      destination: undefined,
      booking_method: undefined,
      
      // Clear event consultation variables
      event_type: undefined,
      consultation_method: undefined,
      service_category: undefined,
      
      // Clear Calendly variables (will be reset below)
      calendly_action: undefined,
      calendly_location: undefined,
    });
    
    // Push the event
    window.dataLayer.push({
      event: 'calendly_embed_interaction',
      calendly_action: action,
      event_type: eventType,
      calendly_location: location,
      page_path: window.location.pathname,
      user_language: getCurrentLanguage(),
    });
  }
};

/**
 * Track consultation scheduled (lead capture)
 * Tracks when a user successfully schedules a consultation via Calendly
 * This is a lead capture event, not a final conversion (actual event booking comes later)
 * Preserves context variables (button_text, button_location, event_type, parent_modal)
 * 
 * @param eventDetails - Details about the scheduled consultation from Calendly
 */
export const trackConsultationScheduled = (
  eventDetails: {
    eventUri?: string;
    inviteeUri?: string;
    eventTypeName?: string;
  }
): void => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    
    // ============================================
    // STEP 1: CLEAR ONLY NON-CONTEXT VARIABLES
    // ============================================
    // NOTE: We preserve button_text, button_location, event_type, and parent_modal
    // to maintain attribution chain from initial click → modal → lead capture
    window.dataLayer.push({
      // PRESERVE button tracking variables for attribution
      // button_text: undefined,       // ← Keep! Shows which button led to lead capture
      // button_location: undefined,   // ← Keep! Shows which section drove lead capture
      
      // Clear modal tracking variables (consultation scheduled, modal irrelevant)
      modal_type: undefined,
      modal_name: undefined,
      // parent_modal: undefined,      // ← Keep! Shows if scheduled from nested modal
      page_location: undefined,
      
      // Clear room booking variables (not relevant for event consultation)
      room_type: undefined,
      
      // Clear contact action variables
      contact_type: undefined,
      contact_source: undefined,
      event_category: undefined,
      event_label: undefined,
      phone_number: undefined,
      email_address: undefined,
      whatsapp_number: undefined,
      region: undefined,
      destination: undefined,
      booking_method: undefined,
      
      // PRESERVE event consultation variables
      // event_type: undefined,        // ← Keep! Critical for attribution
      consultation_method: undefined,
      service_category: undefined,
      
      // Clear Calendly embed tracking variables
      calendly_action: undefined,
      calendly_location: undefined,
    });
    
    // ============================================
    // STEP 2: PUSH THE LEAD CAPTURE EVENT
    // ============================================
    window.dataLayer.push({
      event: 'consultation_scheduled',
      lead_source: 'calendly',
      lead_type: 'event_consultation',
      // event_type is preserved from context (wedding, corporate, private_party, general)
      // button_text is preserved from context
      // button_location is preserved from context
      // parent_modal is preserved from context (if applicable)
      ...(eventDetails.eventUri && { calendly_event_uri: eventDetails.eventUri }),
      ...(eventDetails.inviteeUri && { calendly_invitee_uri: eventDetails.inviteeUri }),
      ...(eventDetails.eventTypeName && { calendly_event_type_name: eventDetails.eventTypeName }),
      page_path: window.location.pathname,
      user_language: getCurrentLanguage(),
    });
  }
};

